/**
 * Dates for cards Mia posts straight to someone (docs/post-direct.md, "Arriving by a date").
 * Loaded on every page (layout/theme.liquid) as window.LphPostDates, for the product page, the
 * basket and the multipack builder.
 *
 * - Making: Mia posts a card within MAKE_DAYS working days of the order.
 * - Royal Mail: allow TRANSIT working days (cautious: 1st Class aims for the next day, 2nd Class
 *   for 2–3).
 * - Working days: Monday–Friday except bank holidays in England and Wales (BANK_HOLIDAYS: add each
 *   new year's from https://www.gov.uk/bank-holidays).
 * - A customer's "arrive by" date gives Mia a "post by" date (the latest day that still gets it
 *   there); she can post any time before. Stored on the card: "Arrive by" (shown, e.g. "Thu 24 Dec
 *   2026") and "_post_by" (hidden, YYYY-MM-DD).
 * - Dates are plain days (YYYY-MM-DD strings, worked on as UTC midnights), "today" is London's.
 */
(() => {
  const MAKE_DAYS = 2;
  const TRANSIT = { first: 2, second: 5 };
  const MAX_MONTHS = 12;
  const BANK_HOLIDAYS = new Set([
    '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25', '2026-08-31', '2026-12-25', '2026-12-28',
    '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28',
    '2028-01-03', '2028-04-14', '2028-04-17', '2028-05-01', '2028-05-29', '2028-08-28', '2028-12-25', '2028-12-26',
  ]);
  const DAY = 86_400_000;

  const toDate = (iso) => new Date(`${iso}T00:00:00Z`);
  const iso = (date) => date.toISOString().slice(0, 10);
  const shift = (day, days) => iso(new Date(toDate(day).getTime() + days * DAY));

  const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London' }).format(new Date());

  const isWorking = (day) => {
    const weekday = toDate(day).getUTCDay();
    return weekday !== 0 && weekday !== 6 && !BANK_HOLIDAYS.has(day);
  };

  /** n working days after day. */
  const addWorking = (day, n) => {
    let d = day;
    for (let left = n; left > 0; ) {
      d = shift(d, 1);
      if (isWorking(d)) left--;
    }
    return d;
  };

  /** What 1st Class adds to a posted card, from the shop's price list (window.LphPrices, layout). */
  const firstExtra = () => `£${((window.LphPrices?.posted ?? 180) / 100).toFixed(2)}`;

  /** 'first' or 'second' from a "Message inside" value or variant title ("…posted 1st Class…"). */
  const postage = (value) => (/1st/i.test(value ?? '') ? 'first' : 'second');

  /** The first day Mia could post a card ordered today. */
  const soonestPost = (from = today()) => addWorking(from, MAKE_DAYS);

  /** The earliest "arrive by" date that's safe with this postage. */
  const earliest = (kind, from = today()) => addWorking(soonestPost(from), TRANSIT[kind] ?? TRANSIT.second);

  /** The furthest ahead a date can be. */
  const latest = (from = today()) => {
    const d = toDate(from);
    d.setUTCMonth(d.getUTCMonth() + MAX_MONTHS);
    return iso(d);
  };

  /** The last working day Mia can post it and still have it there by arriveBy. */
  const postBy = (arriveBy, kind) => {
    let d = arriveBy;
    while (!isWorking(d) || addWorking(d, TRANSIT[kind] ?? TRANSIT.second) > arriveBy) d = shift(d, -1);
    return d;
  };

  const format = (day, { year = true } = {}) =>
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'UTC',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      ...(year ? { year: 'numeric' } : {}),
    })
      .format(toDate(day))
      .replace(',', '');

  /** Is this arrive-by date possible (with this postage, ordering today)? */
  const check = (arriveBy, kind, from = today()) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(arriveBy ?? '')) return 'Please choose a date.';
    if (arriveBy < earliest(kind, from)) return `The earliest it can arrive is ${format(earliest(kind, from), { year: false })}.`;
    if (arriveBy > latest(from)) return 'Please choose a date within the next 12 months.';
    return '';
  };

  /**
   * What to say under the date box: { text, bad, keep }. bad: it can't be added like this; keep: the
   * date can stay (switching to 1st Class fixes it), otherwise it's cleared.
   */
  const explain = (arriveBy, kind, from = today()) => {
    const short = (day) => format(day, { year: false });
    const first = earliest('first', from);
    const second = earliest('second', from);
    if (!arriveBy) {
      return {
        text: kind === 'first' ? `The earliest is ${short(first)} with 1st Class.` : `The earliest is ${short(second)}. Choose 1st Class above (+${firstExtra()}) for ${short(first)}.`,
        bad: false,
        keep: true,
      };
    }
    const problem = check(arriveBy, kind, from);
    if (!problem) return { text: `I'll post it by ${short(postBy(arriveBy, kind))}, so it arrives in time.`, bad: false, keep: true };
    if (kind === 'second' && !check(arriveBy, 'first', from)) {
      return { text: `2nd Class can't get it there by ${short(arriveBy)}. Choose 1st Class above (+${firstExtra()}) and it can.`, bad: true, keep: true };
    }
    // Too soon (or past): say the earliest dates. Too far ahead: say so.
    const tooSoon = arriveBy < earliest(kind, from);
    return { text: tooSoon ? `That's too soon. ${explain('', kind, from).text}` : problem, bad: true, keep: false };
  };

  window.LphPostDates = { MAKE_DAYS, TRANSIT, today, isWorking, addWorking, postage, soonestPost, earliest, latest, postBy, format, check, explain };
})();
